import { INSTALLMENTS_MAX_AMOUNT, INSTALLMENTS_MIN_AMOUNT, withFieldGroup } from '@massilia/features'
import { RadioGroup } from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import { PaymentMethodOption } from '@/components/shared/payment-method-option'
import {
  AGENCY_LOCATION_LABEL,
  AGENCY_OPENING_HOURS_LABEL,
  AGENCY_PHONE,
} from '@/lib/constants/agency'

import type { CheckoutFormValues, PaymentMethodFormValue } from '../checkout-form.schema'

import { paymentMethods } from '../checkout-form.schema'
import { BankTransferDetails } from './bank-transfer-details'
import { InstallmentsSubForm } from './installments-sub-form'

interface PaymentMethodSelectorProps {
  /** Prestataire courant du paiement en plusieurs fois (flag runtime serveur) */
  installmentProvider: 'FLOA' | 'SSP'
  isInstallmentsDisabled?: boolean
  isReunion?: boolean
  paymentDeadline?: string
  totalPrice: number
}

function isPaymentMethod(value: string): value is PaymentMethodFormValue {
  return paymentMethods.includes(value as PaymentMethodFormValue)
}

export const PaymentMethodSelector = withFieldGroup({
  defaultValues: {} as Pick<
    CheckoutFormValues,
    | 'billingCity'
    | 'billingCountryCode'
    | 'billingStreet'
    | 'billingZipCode'
    | 'floaPayType'
    | 'paymentMethod'
  >,
  props: {} as PaymentMethodSelectorProps,
  render: function PaymentMethodFields({
    group,
    installmentProvider,
    isInstallmentsDisabled,
    isReunion,
    paymentDeadline,
    totalPrice,
  }) {
    const deadlineFormatted = paymentDeadline
      ? format(paymentDeadline, 'd MMMM yyyy', { locale: fr })
      : null

    const isInstallmentsEligible =
      totalPrice >= INSTALLMENTS_MIN_AMOUNT && totalPrice <= INSTALLMENTS_MAX_AMOUNT

    return (
      <div className="flex w-full flex-col gap-4">
        <h3 className="text-gray-light-900 font-semibold">Quel est votre moyen de paiement&nbsp;?</h3>
        <group.AppField name="paymentMethod">
          {(field) => {
            const selected = field.state.value

            return (
              <RadioGroup
                className="flex flex-col gap-0"
                onValueChange={(value) => {
                  if (
                    isPaymentMethod(value) &&
                    !(value === 'installments' && isInstallmentsDisabled)
                  ) {
                    field.handleChange(value)
                  }
                }}
                value={selected}
              >
                {/* Carte bancaire */}
                <PaymentMethodOption
                  description={
                    <div className="flex flex-col gap-2">
                      <p className="text-gray-light-700 text-sm">
                        Vous serez redirigé vers notre plateforme de paiement sécurisée pour saisir
                        vos informations de carte bancaire.
                      </p>
                      <p className="text-warning-foreground text-sm font-medium">
                        Des frais de 2 % s&apos;appliqueront a posteriori sur les cartes American
                        Express.
                      </p>
                    </div>
                  }
                  id="credit-card"
                  isFirst
                  isSelected={selected === 'credit-card'}
                  label="Carte bancaire"
                  nextIsSelected={selected === 'installments'}
                  value="credit-card"
                />

                {/* Paiement en plusieurs fois (FLOA ou Oney via SSP selon le flag) */}
                <PaymentMethodOption
                  description={
                    isInstallmentsEligible ? (
                      <InstallmentsSubForm
                        fields={{
                          billingCity: 'billingCity',
                          billingCountryCode: 'billingCountryCode',
                          billingStreet: 'billingStreet',
                          billingZipCode: 'billingZipCode',
                          floaPayType: 'floaPayType',
                        }}
                        form={group}
                        provider={installmentProvider}
                      />
                    ) : (
                      <p className="text-gray-light-700 text-sm">
                        Le paiement en 3/4x n&apos;est pas disponible pour ce montant (entre 50 € et
                        6 000 €).
                      </p>
                    )
                  }
                  disabled={isInstallmentsDisabled || !isInstallmentsEligible}
                  disabledHint={
                    <p className="text-gray-light-500 text-xs">
                      Le paiement en 3/4x n&apos;est pas disponible pour ce montant (entre 50 € et
                      6 000 €).
                    </p>
                  }
                  disabledMessage={
                    isInstallmentsDisabled
                      ? '(Paiement en 3/4x indisponible pour ce type de voyage)'
                      : undefined
                  }
                  id="installments"
                  isSelected={
                    !isInstallmentsDisabled && isInstallmentsEligible && selected === 'installments'
                  }
                  label={
                    installmentProvider === 'FLOA'
                      ? 'Paiement en 3/4x avec FLOA (avec frais)'
                      : 'Paiement en 3/4x avec Oney (avec frais)'
                  }
                  prevIsSelected={selected === 'credit-card'}
                  nextIsSelected={selected === 'vacation-cheque'}
                  value="installments"
                />

                {/* Chèque vacances */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-4">
                      {deadlineFormatted && (
                        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-gray-light-700 text-sm font-medium">
                            Date limite de réception des chèques vacances
                          </div>
                          <div className="text-gray-light-900 text-sm font-semibold">
                            {deadlineFormatted}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="text-gray-light-900 text-sm font-semibold">
                          Faites-les parvenir par courrier recommandé à l&apos;agence.
                        </div>
                        <div className="text-gray-light-900 text-sm">
                          Nous vous informerons de la réception du courrier.
                        </div>
                      </div>
                      <div className="text-gray-light-900 text-sm font-semibold">
                        {AGENCY_LOCATION_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="vacation-cheque"
                  isSelected={selected === 'vacation-cheque'}
                  label="Chèque vacances"
                  prevIsSelected={selected === 'installments'}
                  nextIsSelected={selected === 'vacation-e-cheque'}
                  value="vacation-cheque"
                />

                {/* E-chèque vacances */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-4">
                      {deadlineFormatted && (
                        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-gray-light-700 text-sm font-medium">
                            Date limite de réception des E-chèques vacances
                          </div>
                          <div className="text-gray-light-900 text-sm font-semibold">
                            {deadlineFormatted}
                          </div>
                        </div>
                      )}
                      <div className="text-gray-light-900 text-sm font-semibold">
                        Appelez l&apos;agence aux horaires d&apos;ouverture.
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="vacation-e-cheque"
                  isSelected={selected === 'vacation-e-cheque'}
                  label="E-chèque vacances"
                  prevIsSelected={selected === 'vacation-cheque'}
                  nextIsSelected={selected === 'bank-transfer'}
                  value="vacation-e-cheque"
                />

                {/* Virement bancaire */}
                <PaymentMethodOption
                  description={
                    <BankTransferDetails paymentDeadline={paymentDeadline} isReunion={isReunion} />
                  }
                  id="bank-transfer"
                  isSelected={selected === 'bank-transfer'}
                  label="Virement bancaire"
                  prevIsSelected={selected === 'vacation-e-cheque'}
                  nextIsSelected={selected === 'cash'}
                  value="bank-transfer"
                />

                {/* Espèces */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-3">
                      <div className="text-gray-light-900 text-sm font-semibold">
                        {AGENCY_LOCATION_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="cash"
                  isLast
                  isSelected={selected === 'cash'}
                  label="Espèces (uniquement en agence)"
                  prevIsSelected={selected === 'bank-transfer'}
                  value="cash"
                />
              </RadioGroup>
            )
          }}
        </group.AppField>
      </div>
    )
  },
})
